import { createRoot, type Root } from "react-dom/client";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import App from "./App.tsx";
import "./index.css";

// Recover from stale lazy-chunk URLs after a redeploy by reloading once.
const RELOAD_KEY = "__chunk_reload__";
function isChunkLoadError(err: unknown): boolean {
  const msg = (err as any)?.message ?? String(err ?? "");
  return (
    /Importing a module script failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg)
  );
}
function maybeReload(err: unknown) {
  if (!isChunkLoadError(err)) return;
  if (sessionStorage.getItem(RELOAD_KEY)) return;
  sessionStorage.setItem(RELOAD_KEY, "1");
  window.location.reload();
}
window.addEventListener("error", (e) => maybeReload(e.error ?? e.message));
window.addEventListener("unhandledrejection", (e) => maybeReload(e.reason));
window.addEventListener("load", () => {
  // Clear the guard once a fresh load succeeds.
  setTimeout(() => sessionStorage.removeItem(RELOAD_KEY), 5000);
});

declare global {
  interface Window {
    __liftblickReactRoot?: Root;
  }
}

const container = document.getElementById("root");

if (!container) {
  throw new Error("Liftblick root element not found");
}

// The preview can load route-level static HTML and the React app during hot updates.
// Keep exactly one React root so the wizard cannot be mounted twice in the same DOM.
if (!window.__liftblickReactRoot) {
  container.replaceChildren();
  window.__liftblickReactRoot = createRoot(container);
}

window.__liftblickReactRoot.render(<App />);
